/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * CDF Template API
 * OpenAPI-first contract for the CDF template.

Workflow:
  1. Edit this spec FIRST for any API change.
  2. Run `pnpm gen:api` to regenerate the React Query client
     (lib/api-client-react) and Zod schemas (lib/api-zod).
  3. Implement/adjust the Fastify routes in artifacts/api-server
     to match, validating with the generated Zod schemas.

 * OpenAPI spec version: 0.1.0
 */
import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import { customFetch } from '../mutator';
export type HealthStatus = typeof HealthStatus[keyof typeof HealthStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const HealthStatus = {
  ok: 'ok',
} as const;

export type HealthDatabase = typeof HealthDatabase[keyof typeof HealthDatabase];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const HealthDatabase = {
  connected: 'connected',
  not_configured: 'not_configured',
} as const;

export type HealthPlugins = {
  database: boolean;
  storage: boolean;
  ai: boolean;
  auth: boolean;
};

export interface Health {
  status: HealthStatus;
  uptimeSeconds: number;
  timestamp: string;
  database: HealthDatabase;
  plugins: HealthPlugins;
}

export type PluginStatusValue = typeof PluginStatusValue[keyof typeof PluginStatusValue];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PluginStatusValue = {
  ready: 'ready',
  disabled: 'disabled',
  misconfigured: 'misconfigured',
} as const;

export type DatabasePluginCapabilities = {
  persistentStorage: boolean;
  appUsers: boolean;
};

export interface DatabasePlugin {
  enabled: boolean;
  status: PluginStatusValue;
  reason?: string;
  capabilities: DatabasePluginCapabilities;
}

export type StoragePluginCapabilities = {
  fileUpload: boolean;
  fileDownload: boolean;
};

export interface StoragePlugin {
  enabled: boolean;
  status: PluginStatusValue;
  reason?: string;
  capabilities: StoragePluginCapabilities;
}

export type AiPluginCapabilities = {
  llm: boolean;
  embeddings: boolean;
  semanticSearch: boolean;
};

export interface AiPlugin {
  enabled: boolean;
  status: PluginStatusValue;
  reason?: string;
  capabilities: AiPluginCapabilities;
}

/**
 * Public MSAL client configuration (no secrets).
 */
export interface AuthClientConfig {
  clientId: string;
  authority: string;
  redirectUri: string;
  postLoginRedirect: string;
  scopes: string[];
}

export type AuthPluginProvider = typeof AuthPluginProvider[keyof typeof AuthPluginProvider];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AuthPluginProvider = {
  microsoft_entra_workforce: 'microsoft_entra_workforce',
} as const;

export type AuthPluginCapabilities = {
  login: boolean;
  protectedRoutes: boolean;
  persistentUsers: boolean;
};

export interface AuthPlugin {
  enabled: boolean;
  status: PluginStatusValue;
  reason?: string;
  provider?: AuthPluginProvider;
  mode?: string;
  capabilities: AuthPluginCapabilities;
  config?: AuthClientConfig;
}

export interface PluginsStatus {
  database: DatabasePlugin;
  storage: StoragePlugin;
  ai: AiPlugin;
  auth: AuthPlugin;
}

/**
 * Application role assigned by an administrator (not from Entra claims)
 */
export type AppUserRole = typeof AppUserRole[keyof typeof AppUserRole];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AppUserRole = {
  admin: 'admin',
  teacher: 'teacher',
  parent: 'parent',
  student: 'student',
  pending: 'pending',
} as const;

/**
 * Persisted application role when the user is synced; null when DB is dormant
 * @nullable
 */
export type CurrentUserRole = AppUserRole | null;

export interface CurrentUser {
  /** @nullable */
  appUserId?: string | null;
  entraObjectId: string;
  entraTenantId: string;
  /** @nullable */
  email?: string | null;
  displayName: string;
  /** True when the user is synced to the app_users table. */
  persistent: boolean;
  /**
   * Persisted application role when the user is synced; null when DB is dormant
   * @nullable
   */
  role?: CurrentUserRole;
  /**
   * Whether the account is active; null when DB is dormant
   * @nullable
   */
  isActive?: boolean | null;
}

export interface AdminUser {
  id: string;
  /** @nullable */
  email?: string | null;
  displayName: string;
  role: AppUserRole;
  isActive: boolean;
  /** @nullable */
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminUserList {
  users: AdminUser[];
  /** @minimum 0 */
  total: number;
}

/**
 * At least one of role or isActive must be provided
 */
export interface UpdateAdminUserRequest {
  role?: AppUserRole;
  isActive?: boolean;
}

export interface ChatRequest {
  /**
   * @minLength 1
   * @maxLength 4000
   */
  message: string;
  conversationId?: string;
}

export interface ChatResponse {
  reply: string;
  conversationId: string;
}

export interface StoredFile {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
  /** @nullable */
  ownerDisplayName?: string | null;
}

export interface FileList {
  files: StoredFile[];
  /** @minimum 0 */
  total: number;
}

export interface DashboardStats {
  totalItems: number;
  activeItems: number;
  draftItems: number;
  archivedItems: number;
  createdThisWeek: number;
}

export interface TimeseriesPoint {
  date: string;
  created: number;
  completed: number;
}

export interface Timeseries {
  days: number;
  points: TimeseriesPoint[];
}

export type ItemStatus = typeof ItemStatus[keyof typeof ItemStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ItemStatus = {
  draft: 'draft',
  active: 'active',
  archived: 'archived',
} as const;

export interface Item {
  id: string;
  name: string;
  /** @nullable */
  description: string | null;
  status: ItemStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ItemList {
  items: Item[];
  /** @minimum 0 */
  total: number;
}

export interface ItemCreate {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  /** @nullable */
  description?: string | null;
  status?: ItemStatus;
}

export interface ItemUpdate {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name?: string;
  /** @nullable */
  description?: string | null;
  status?: ItemStatus;
}

export interface Error {
  error: string;
  code?: string;
}

export type StudentStatus = typeof StudentStatus[keyof typeof StudentStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const StudentStatus = {
  active: 'active',
  inactive: 'inactive',
} as const;

export type EventStatus = typeof EventStatus[keyof typeof EventStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const EventStatus = {
  active: 'active',
  completed: 'completed',
} as const;

export type CheckinStatus = typeof CheckinStatus[keyof typeof CheckinStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CheckinStatus = {
  checked_in: 'checked_in',
  signed_out: 'signed_out',
} as const;

export type RollCallStatus = typeof RollCallStatus[keyof typeof RollCallStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const RollCallStatus = {
  pending: 'pending',
  present: 'present',
  absent: 'absent',
} as const;

export type CheckoutType = typeof CheckoutType[keyof typeof CheckoutType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CheckoutType = {
  by_self: 'by_self',
  wait_for_parent: 'wait_for_parent',
  back_to_school: 'back_to_school',
  with_person: 'with_person',
  with_teacher: 'with_teacher',
} as const;

export interface Guardian {
  id: string;
  name: string;
  phoneNumber: string;
}

export interface GuardianUpdate {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name?: string;
  /**
   * E.164 format, e.g. +614…
   * @minLength 1
   */
  phoneNumber?: string;
}

export type StudentDefaultCheckoutType = CheckoutType | null;

export interface Student {
  id: string;
  firstName: string;
  lastInitial: string;
  group: string;
  status: StudentStatus;
  defaultCheckoutType: StudentDefaultCheckoutType;
  /**
   * Authorized person name when defaultCheckoutType is with_person
   * @nullable
   */
  defaultAuthorizedPersonName: string | null;
  createdAt: string;
  guardians?: Guardian[];
}

export type StudentCreateDefaultCheckoutType = CheckoutType | null;

export interface StudentCreate {
  /**
   * @minLength 1
   * @maxLength 100
   */
  firstName: string;
  /**
   * @minLength 1
   * @maxLength 5
   */
  lastInitial: string;
  /**
   * @minLength 1
   * @maxLength 50
   */
  group: string;
  status?: StudentStatus;
  defaultCheckoutType?: StudentCreateDefaultCheckoutType;
  /**
   * Authorized person name when defaultCheckoutType is with_person
   * @maxLength 200
   * @nullable
   */
  defaultAuthorizedPersonName?: string | null;
  /** @maxLength 100 */
  guardianName?: string;
  /** E.164 format, e.g. +614… */
  guardianPhone?: string;
}

export type StudentUpdateStatus = typeof StudentUpdateStatus[keyof typeof StudentUpdateStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const StudentUpdateStatus = {
  active: 'active',
  inactive: 'inactive',
} as const;

export type StudentUpdateDefaultCheckoutType = CheckoutType | null;

export interface StudentUpdate {
  /**
   * @minLength 1
   * @maxLength 100
   */
  firstName?: string;
  /**
   * @minLength 1
   * @maxLength 5
   */
  lastInitial?: string;
  /**
   * @minLength 1
   * @maxLength 50
   */
  group?: string;
  status?: StudentUpdateStatus;
  defaultCheckoutType?: StudentUpdateDefaultCheckoutType;
  /**
   * Authorized person name when defaultCheckoutType is with_person
   * @maxLength 200
   * @nullable
   */
  defaultAuthorizedPersonName?: string | null;
}

export interface StudentList {
  students: Student[];
  /** @minimum 0 */
  total: number;
}

export type StudentImportDetailStatus = typeof StudentImportDetailStatus[keyof typeof StudentImportDetailStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const StudentImportDetailStatus = {
  created: 'created',
  duplicate: 'duplicate',
  error: 'error',
} as const;

export interface StudentImportDetail {
  /** Row number in the CSV (1-based */
  row: number;
  firstName: string;
  lastInitial: string;
  group: string;
  status: StudentImportDetailStatus;
  message: string;
}

export interface StudentImportResult {
  /** @minimum 0 */
  total: number;
  /** @minimum 0 */
  created: number;
  /** @minimum 0 */
  duplicates: number;
  /** @minimum 0 */
  errors: number;
  details: StudentImportDetail[];
}

export interface Event {
  id: string;
  name: string;
  date: string;
  location: string;
  status: EventStatus;
  /**
   * Session start time-of-day, e.g. "12:30"
   * @nullable
   */
  startTime: string | null;
  /**
   * Session end time-of-day, e.g. "15:00"
   * @nullable
   */
  endTime: string | null;
  /**
   * Shared identifier linking occurrences of a recurring series.
   * @nullable
   */
  seriesId: string | null;
  createdAt: string;
}

/**
 * Recurrence frequency (only weekly is supported).
 */
export type EventRecurrenceFrequency = typeof EventRecurrenceFrequency[keyof typeof EventRecurrenceFrequency];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const EventRecurrenceFrequency = {
  weekly: 'weekly',
} as const;

export interface EventRecurrence {
  /** Recurrence frequency (only weekly is supported). */
  frequency: EventRecurrenceFrequency;
  /**
   * Number of weekly occurrences to create (including the first).
   * @minimum 1
   * @maximum 52
   */
  count: number;
}

export interface EventCreate {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  date: string;
  /**
   * @minLength 1
   * @maxLength 200
   */
  location: string;
  /** Session start time-of-day, e.g. "12:30" */
  startTime?: string;
  /** Session end time-of-day, e.g. "15:00" */
  endTime?: string;
  recurrence?: EventRecurrence;
  studentIds?: string[];
  /** Ordered list of roll call names to pre-plan for the event. */
  rollCalls?: string[];
}

export interface EventUpdate {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name?: string;
  status?: EventStatus;
}

export interface EventList {
  events: Event[];
  /** @minimum 0 */
  total: number;
}

/**
 * @nullable
 */
export type AttendanceRecordCheckoutType = CheckoutType | null;

/**
 * @nullable
 */
export type AttendanceRecordSmsPreference = CheckoutType | null;

export interface AttendanceRecord {
  id: string;
  eventId: string;
  studentId: string;
  student?: Student;
  checkinStatus: CheckinStatus;
  /** @nullable */
  checkoutType?: AttendanceRecordCheckoutType;
  /** @nullable */
  smsPreference?: AttendanceRecordSmsPreference;
  /**
   * Name of the authorized person when smsPreference is with_person.
   * @nullable
   */
  authorizedPersonName?: string | null;
  /** @nullable */
  signedOutBy?: string | null;
  rollCallStatus: RollCallStatus;
  /** @nullable */
  rollCallMarkedBy?: string | null;
  /** @nullable */
  rollCallMarkedAt?: string | null;
  checkedInAt: string;
  lastUpdatedAt: string;
}

export type AttendanceUpdateCheckinStatus = typeof AttendanceUpdateCheckinStatus[keyof typeof AttendanceUpdateCheckinStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AttendanceUpdateCheckinStatus = {
  checked_in: 'checked_in',
  signed_out: 'signed_out',
} as const;

export type AttendanceUpdateRollCallStatus = typeof AttendanceUpdateRollCallStatus[keyof typeof AttendanceUpdateRollCallStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AttendanceUpdateRollCallStatus = {
  pending: 'pending',
  present: 'present',
  absent: 'absent',
} as const;

/**
 * Partial update. Roll-call fields and sign-out fields are independent — setting rollCallStatus does not require or change checkinStatus, and vice versa. Enums are inlined (no defaults) so omitted fields stay unchanged — $ref defaults would otherwise force checkinStatus=checked_in / rollCallStatus=pending on every PATCH.

 */
export interface AttendanceUpdate {
  checkinStatus?: AttendanceUpdateCheckinStatus;
  checkoutType?: CheckoutType;
  /** @maxLength 100 */
  signedOutBy?: string;
  /**
   * Name of the collecting person when checkoutType or smsPreference is with_person.
   * @maxLength 100
   * @nullable
   */
  authorizedPersonName?: string | null;
  rollCallStatus?: AttendanceUpdateRollCallStatus;
  /** @maxLength 100 */
  rollCallMarkedBy?: string;
}

export interface AttendanceSettings {
  /** Master switch for voice recognition on the register view */
  voiceEnabled: boolean;
}

export interface AttendanceList {
  attendance: AttendanceRecord[];
  /** @minimum 0 */
  total: number;
}

/**
 * @nullable
 */
export type SmsLogParsedPreference = CheckoutType | null;

export interface SmsLog {
  id: string;
  senderPhone: string;
  messageBody: string;
  receivedAt: string;
  /** @nullable */
  matchedStudentId?: string | null;
  /** @nullable */
  matchedGuardianId?: string | null;
  /** @nullable */
  eventId?: string | null;
  /** @nullable */
  parsedPreference?: SmsLogParsedPreference;
  /**
   * Name of the authorized person when parsedPreference is with_person.
   * @nullable
   */
  authorizedPersonName?: string | null;
  isValidated: boolean;
}

export interface SmsIncoming {
  /** Sender phone (Twilio E.164 or local) */
  From: string;
  Body: string;
  MessageSid?: string;
}

export interface SmsLogList {
  logs: SmsLog[];
  /** @minimum 0 */
  total: number;
}

export interface Volunteer {
  id: string;
  name: string;
  phone: string;
  wwccNumber: string;
  wwccExpiry: string;
  isInducted: boolean;
  /** @nullable */
  eventId?: string | null;
  createdAt: string;
}

export interface VolunteerCreate {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  /** @minLength 1 */
  phone: string;
  /** @minLength 1 */
  wwccNumber: string;
  wwccExpiry: string;
  isInducted?: boolean;
  eventId?: string;
}

export interface VolunteerUpdate {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name?: string;
  /** @minLength 1 */
  phone?: string;
  /** @minLength 1 */
  wwccNumber?: string;
  wwccExpiry?: string;
  isInducted?: boolean;
  eventId?: string;
}

export interface VolunteerList {
  volunteers: Volunteer[];
  /** @minimum 0 */
  total: number;
}

export type AuditEntryType = typeof AuditEntryType[keyof typeof AuditEntryType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AuditEntryType = {
  signout: 'signout',
  sms: 'sms',
  roll_call: 'roll_call',
} as const;

/**
 * @nullable
 */
export type AuditEntryCheckoutType = CheckoutType | null;

/**
 * @nullable
 */
export type AuditEntryRollCallStatus = RollCallStatus | null;

export interface AuditEntry {
  type: AuditEntryType;
  timestamp: string;
  /** @nullable */
  studentName?: string | null;
  /** @nullable */
  eventName?: string | null;
  /** @nullable */
  actor?: string | null;
  /** @nullable */
  action?: string | null;
  /** @nullable */
  checkoutType?: AuditEntryCheckoutType;
  /** @nullable */
  senderPhone?: string | null;
  /** @nullable */
  messageBody?: string | null;
  /** @nullable */
  isValidated?: boolean | null;
  /** @nullable */
  rollCallStatus?: AuditEntryRollCallStatus;
  /** @nullable */
  authorizedPersonName?: string | null;
}

export interface AuditList {
  entries: AuditEntry[];
  /** @minimum 0 */
  total: number;
}

export type PhoneApprovalStatus = typeof PhoneApprovalStatus[keyof typeof PhoneApprovalStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PhoneApprovalStatus = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
} as const;

/**
 * @nullable
 */
export type PhoneApprovalRequestLatestParsedPreference = CheckoutType | null;

export interface PhoneApprovalRequest {
  id: string;
  phoneNumber: string;
  status: PhoneApprovalStatus;
  /** @nullable */
  latestParsedPreference: PhoneApprovalRequestLatestParsedPreference;
  /**
   * Name of the authorized person when latestParsedPreference is with_person.
   * @nullable
   */
  latestAuthorizedPersonName?: string | null;
  /** @nullable */
  reviewerId: string | null;
  /** @nullable */
  reviewerName: string | null;
  /** @nullable */
  reviewedAt: string | null;
  /** @nullable */
  guardianId: string | null;
  createdAt: string;
  updatedAt: string;
}

export type PhoneApprovalDetailAllOf = {
  messages: SmsLog[];
};

export type PhoneApprovalDetail = PhoneApprovalRequest & PhoneApprovalDetailAllOf;

export interface PhoneApprovalList {
  requests: PhoneApprovalDetail[];
  /** @minimum 0 */
  total: number;
}

export interface ApprovePhoneBody {
  /**
   * Name for the new guardian. Required if guardianId is not provided.
   * @maxLength 100
   */
  name?: string;
  /** Existing guardian to attach this phone number to. If provided, name is ignored. */
  guardianId?: string;
  /**
   * One or more student IDs to link to this guardian.
   * @minItems 1
   */
  studentIds: string[];
}

export type ApprovePhoneResultGuardian = {
  id: string;
  name: string;
  phoneNumber: string;
};

export interface ApprovePhoneResult {
  request: PhoneApprovalRequest;
  guardian: ApprovePhoneResultGuardian;
  updatedAttendance: AttendanceRecord[];
}

export interface ApprovedNumberStudent {
  id: string;
  firstName: string;
  lastInitial: string;
  group: string;
}

export interface ApprovedNumber {
  id: string;
  name: string;
  phoneNumber: string;
  students: ApprovedNumberStudent[];
}

export interface ApprovedNumberList {
  guardians: ApprovedNumber[];
  /** @minimum 0 */
  total: number;
}

export interface RollCall {
  id: string;
  eventId: string;
  name: string;
  /** @minimum 0 */
  sortOrder: number;
  /** @nullable */
  createdBy: string | null;
  createdAt: string;
}

export interface RollCallCreate {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  /** @maxLength 100 */
  createdBy?: string;
}

export interface RollCallUpdate {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name?: string;
}

export interface RollCallList {
  rollCalls: RollCall[];
  /** @minimum 0 */
  total: number;
}

export interface RollCallEntry {
  id: string;
  rollCallId: string;
  studentId: string;
  status: RollCallStatus;
  /** @nullable */
  markedBy: string | null;
  /** @nullable */
  markedAt: string | null;
  student?: Student;
}

export interface RollCallEntryUpdate {
  status: RollCallStatus;
  /** @maxLength 100 */
  markedBy?: string;
}

export interface RollCallEntryList {
  entries: RollCallEntry[];
  /** @minimum 0 */
  total: number;
}

export type GetDashboardTimeseriesParams = {
/**
 * @minimum 7
 * @maximum 90
 */
days?: number;
};

export type ListItemsParams = {
status?: ItemStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListAdminUsersParams = {
/**
 * Case-insensitive filter on display name, email, or role
 * @maxLength 200
 */
q?: string;
};

export type UploadFileBody = {
  file: Blob;
};

export type ListEventsParams = {
status?: EventStatus;
};

export type ListStudentsParams = {
group?: string;
};

export type ImportStudentsBody = {
  /** CSV file with columns firstName, lastInitial, group (required) and guardianName, guardianPhone (optional) */
  file: Blob;
};

export type ListVolunteersParams = {
eventId?: string;
};

export type ListAuditSignoutsParams = {
eventId?: string;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
};

export type ListPhoneApprovalsParams = {
status?: ListPhoneApprovalsStatus;
};

export type ListPhoneApprovalsStatus = typeof ListPhoneApprovalsStatus[keyof typeof ListPhoneApprovalsStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ListPhoneApprovalsStatus = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
} as const;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



/**
 * @summary Liveness probe
 */
export const getGetHealthUrl = () => {


  

  return `/api/health`
}

export const getHealth = async ( options?: RequestInit): Promise<Health> => {
  
  return customFetch<Health>(getGetHealthUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetHealthQueryKey = () => {
    return [
    `/api/health`
    ] as const;
    }

    
export const getGetHealthQueryOptions = <TData = Awaited<ReturnType<typeof getHealth>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHealthQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHealth>>> = ({ signal }) => getHealth({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData> & { queryKey: QueryKey }
}

export type GetHealthQueryResult = NonNullable<Awaited<ReturnType<typeof getHealth>>>
export type GetHealthQueryError = unknown


/**
 * @summary Liveness probe
 */

export function useGetHealth<TData = Awaited<ReturnType<typeof getHealth>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetHealthQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Aggregate counters for the dashboard stat cards
 */
export const getGetDashboardStatsUrl = () => {


  

  return `/api/dashboard/stats`
}

export const getDashboardStats = async ( options?: RequestInit): Promise<DashboardStats> => {
  
  return customFetch<DashboardStats>(getGetDashboardStatsUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetDashboardStatsQueryKey = () => {
    return [
    `/api/dashboard/stats`
    ] as const;
    }

    
export const getGetDashboardStatsQueryOptions = <TData = Awaited<ReturnType<typeof getDashboardStats>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDashboardStatsQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardStats>>> = ({ signal }) => getDashboardStats({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDashboardStatsQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardStats>>>
export type GetDashboardStatsQueryError = unknown


/**
 * @summary Aggregate counters for the dashboard stat cards
 */

export function useGetDashboardStats<TData = Awaited<ReturnType<typeof getDashboardStats>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDashboardStatsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Daily activity series for the dashboard chart
 */
export const getGetDashboardTimeseriesUrl = (params?: GetDashboardTimeseriesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/dashboard/timeseries?${stringifiedParams}` : `/api/dashboard/timeseries`
}

export const getDashboardTimeseries = async (params?: GetDashboardTimeseriesParams, options?: RequestInit): Promise<Timeseries> => {
  
  return customFetch<Timeseries>(getGetDashboardTimeseriesUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetDashboardTimeseriesQueryKey = (params?: GetDashboardTimeseriesParams,) => {
    return [
    `/api/dashboard/timeseries`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getGetDashboardTimeseriesQueryOptions = <TData = Awaited<ReturnType<typeof getDashboardTimeseries>>, TError = unknown>(params?: GetDashboardTimeseriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDashboardTimeseriesQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardTimeseries>>> = ({ signal }) => getDashboardTimeseries(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDashboardTimeseriesQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardTimeseries>>>
export type GetDashboardTimeseriesQueryError = unknown


/**
 * @summary Daily activity series for the dashboard chart
 */

export function useGetDashboardTimeseries<TData = Awaited<ReturnType<typeof getDashboardTimeseries>>, TError = unknown>(
 params?: GetDashboardTimeseriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDashboardTimeseriesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary List items
 */
export const getListItemsUrl = (params?: ListItemsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/items?${stringifiedParams}` : `/api/items`
}

export const listItems = async (params?: ListItemsParams, options?: RequestInit): Promise<ItemList> => {
  
  return customFetch<ItemList>(getListItemsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListItemsQueryKey = (params?: ListItemsParams,) => {
    return [
    `/api/items`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getListItemsQueryOptions = <TData = Awaited<ReturnType<typeof listItems>>, TError = unknown>(params?: ListItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListItemsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listItems>>> = ({ signal }) => listItems(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listItems>>, TError, TData> & { queryKey: QueryKey }
}

export type ListItemsQueryResult = NonNullable<Awaited<ReturnType<typeof listItems>>>
export type ListItemsQueryError = unknown


/**
 * @summary List items
 */

export function useListItems<TData = Awaited<ReturnType<typeof listItems>>, TError = unknown>(
 params?: ListItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListItemsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Create an item
 */
export const getCreateItemUrl = () => {


  

  return `/api/items`
}

export const createItem = async (itemCreate: ItemCreate, options?: RequestInit): Promise<Item> => {
  
  return customFetch<Item>(getCreateItemUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      itemCreate,)
  }
);}




export const getCreateItemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createItem>>, TError,{data: ItemCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createItem>>, TError,{data: ItemCreate}, TContext> => {

const mutationKey = ['createItem'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createItem>>, {data: ItemCreate}> = (props) => {
          const {data} = props ?? {};

          return  createItem(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateItemMutationResult = NonNullable<Awaited<ReturnType<typeof createItem>>>
    export type CreateItemMutationBody = ItemCreate
    export type CreateItemMutationError = Error

    /**
 * @summary Create an item
 */
export const useCreateItem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createItem>>, TError,{data: ItemCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createItem>>,
        TError,
        {data: ItemCreate},
        TContext
      > => {

      const mutationOptions = getCreateItemMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Get one item
 */
export const getGetItemUrl = (id: string,) => {


  

  return `/api/items/${id}`
}

export const getItem = async (id: string, options?: RequestInit): Promise<Item> => {
  
  return customFetch<Item>(getGetItemUrl(id),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetItemQueryKey = (id?: string,) => {
    return [
    `/api/items/${id}`
    ] as const;
    }

    
export const getGetItemQueryOptions = <TData = Awaited<ReturnType<typeof getItem>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getItem>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetItemQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getItem>>> = ({ signal }) => getItem(id, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getItem>>, TError, TData> & { queryKey: QueryKey }
}

export type GetItemQueryResult = NonNullable<Awaited<ReturnType<typeof getItem>>>
export type GetItemQueryError = Error


/**
 * @summary Get one item
 */

export function useGetItem<TData = Awaited<ReturnType<typeof getItem>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getItem>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetItemQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Update an item
 */
export const getUpdateItemUrl = (id: string,) => {


  

  return `/api/items/${id}`
}

export const updateItem = async (id: string,
    itemUpdate: ItemUpdate, options?: RequestInit): Promise<Item> => {
  
  return customFetch<Item>(getUpdateItemUrl(id),
  {      
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      itemUpdate,)
  }
);}




export const getUpdateItemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateItem>>, TError,{id: string;data: ItemUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateItem>>, TError,{id: string;data: ItemUpdate}, TContext> => {

const mutationKey = ['updateItem'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateItem>>, {id: string;data: ItemUpdate}> = (props) => {
          const {id,data} = props ?? {};

          return  updateItem(id,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateItemMutationResult = NonNullable<Awaited<ReturnType<typeof updateItem>>>
    export type UpdateItemMutationBody = ItemUpdate
    export type UpdateItemMutationError = Error

    /**
 * @summary Update an item
 */
export const useUpdateItem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateItem>>, TError,{id: string;data: ItemUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateItem>>,
        TError,
        {id: string;data: ItemUpdate},
        TContext
      > => {

      const mutationOptions = getUpdateItemMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Delete an item
 */
export const getDeleteItemUrl = (id: string,) => {


  

  return `/api/items/${id}`
}

export const deleteItem = async (id: string, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getDeleteItemUrl(id),
  {      
    ...options,
    method: 'DELETE'
    
    
  }
);}




export const getDeleteItemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteItem>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteItem>>, TError,{id: string}, TContext> => {

const mutationKey = ['deleteItem'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteItem>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  deleteItem(id,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteItemMutationResult = NonNullable<Awaited<ReturnType<typeof deleteItem>>>
    
    export type DeleteItemMutationError = Error

    /**
 * @summary Delete an item
 */
export const useDeleteItem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteItem>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteItem>>,
        TError,
        {id: string},
        TContext
      > => {

      const mutationOptions = getDeleteItemMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Public (unauthenticated) endpoint. Returns the normalized state of the
database, storage, AI, and auth plugins. Only non-secret values are
included (e.g. the MSAL public client config). Never returns keys,
connection strings, or SAS URLs.

 * @summary Availability and public config of platform plugins
 */
export const getGetPluginsStatusUrl = () => {


  

  return `/api/plugins/status`
}

export const getPluginsStatus = async ( options?: RequestInit): Promise<PluginsStatus> => {
  
  return customFetch<PluginsStatus>(getGetPluginsStatusUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetPluginsStatusQueryKey = () => {
    return [
    `/api/plugins/status`
    ] as const;
    }

    
export const getGetPluginsStatusQueryOptions = <TData = Awaited<ReturnType<typeof getPluginsStatus>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPluginsStatusQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPluginsStatus>>> = ({ signal }) => getPluginsStatus({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPluginsStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getPluginsStatus>>>
export type GetPluginsStatusQueryError = unknown


/**
 * @summary Availability and public config of platform plugins
 */

export function useGetPluginsStatus<TData = Awaited<ReturnType<typeof getPluginsStatus>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPluginsStatusQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Current authenticated user
 */
export const getGetCurrentUserUrl = () => {


  

  return `/api/auth/me`
}

export const getCurrentUser = async ( options?: RequestInit): Promise<CurrentUser> => {
  
  return customFetch<CurrentUser>(getGetCurrentUserUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetCurrentUserQueryKey = () => {
    return [
    `/api/auth/me`
    ] as const;
    }

    
export const getGetCurrentUserQueryOptions = <TData = Awaited<ReturnType<typeof getCurrentUser>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCurrentUserQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCurrentUser>>> = ({ signal }) => getCurrentUser({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCurrentUserQueryResult = NonNullable<Awaited<ReturnType<typeof getCurrentUser>>>
export type GetCurrentUserQueryError = Error


/**
 * @summary Current authenticated user
 */

export function useGetCurrentUser<TData = Awaited<ReturnType<typeof getCurrentUser>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCurrentUserQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Returns synced app_users with role and active status.
Requires the admin role when authentication is enabled.

 * @summary List application users for administration
 */
export const getListAdminUsersUrl = (params?: ListAdminUsersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/users?${stringifiedParams}` : `/api/admin/users`
}

export const listAdminUsers = async (params?: ListAdminUsersParams, options?: RequestInit): Promise<AdminUserList> => {
  
  return customFetch<AdminUserList>(getListAdminUsersUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListAdminUsersQueryKey = (params?: ListAdminUsersParams,) => {
    return [
    `/api/admin/users`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getListAdminUsersQueryOptions = <TData = Awaited<ReturnType<typeof listAdminUsers>>, TError = Error>(params?: ListAdminUsersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAdminUsers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAdminUsersQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAdminUsers>>> = ({ signal }) => listAdminUsers(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAdminUsers>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAdminUsersQueryResult = NonNullable<Awaited<ReturnType<typeof listAdminUsers>>>
export type ListAdminUsersQueryError = Error


/**
 * @summary List application users for administration
 */

export function useListAdminUsers<TData = Awaited<ReturnType<typeof listAdminUsers>>, TError = Error>(
 params?: ListAdminUsersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAdminUsers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAdminUsersQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Admins may change another user's role and activate/deactivate accounts.
Self-demotion and self-deactivation are rejected.

 * @summary Update a user's role or active status
 */
export const getUpdateAdminUserUrl = (id: string,) => {


  

  return `/api/admin/users/${id}`
}

export const updateAdminUser = async (id: string,
    updateAdminUserRequest: UpdateAdminUserRequest, options?: RequestInit): Promise<AdminUser> => {
  
  return customFetch<AdminUser>(getUpdateAdminUserUrl(id),
  {      
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      updateAdminUserRequest,)
  }
);}




export const getUpdateAdminUserMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAdminUser>>, TError,{id: string;data: UpdateAdminUserRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAdminUser>>, TError,{id: string;data: UpdateAdminUserRequest}, TContext> => {

const mutationKey = ['updateAdminUser'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAdminUser>>, {id: string;data: UpdateAdminUserRequest}> = (props) => {
          const {id,data} = props ?? {};

          return  updateAdminUser(id,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateAdminUserMutationResult = NonNullable<Awaited<ReturnType<typeof updateAdminUser>>>
    export type UpdateAdminUserMutationBody = UpdateAdminUserRequest
    export type UpdateAdminUserMutationError = Error

    /**
 * @summary Update a user's role or active status
 */
export const useUpdateAdminUser = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAdminUser>>, TError,{id: string;data: UpdateAdminUserRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAdminUser>>,
        TError,
        {id: string;data: UpdateAdminUserRequest},
        TContext
      > => {

      const mutationOptions = getUpdateAdminUserMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Server-side LLM proxy. The client may only send a message and an
optional conversation id — model, endpoint, and credentials are
resolved exclusively on the server.

 * @summary Send a chat message to the app assistant
 */
export const getPostAiChatUrl = () => {


  

  return `/api/ai/chat`
}

export const postAiChat = async (chatRequest: ChatRequest, options?: RequestInit): Promise<ChatResponse> => {
  
  return customFetch<ChatResponse>(getPostAiChatUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      chatRequest,)
  }
);}




export const getPostAiChatMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postAiChat>>, TError,{data: ChatRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postAiChat>>, TError,{data: ChatRequest}, TContext> => {

const mutationKey = ['postAiChat'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postAiChat>>, {data: ChatRequest}> = (props) => {
          const {data} = props ?? {};

          return  postAiChat(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type PostAiChatMutationResult = NonNullable<Awaited<ReturnType<typeof postAiChat>>>
    export type PostAiChatMutationBody = ChatRequest
    export type PostAiChatMutationError = Error

    /**
 * @summary Send a chat message to the app assistant
 */
export const usePostAiChat = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postAiChat>>, TError,{data: ChatRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof postAiChat>>,
        TError,
        {data: ChatRequest},
        TContext
      > => {

      const mutationOptions = getPostAiChatMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary List stored files
 */
export const getListFilesUrl = () => {


  

  return `/api/files`
}

export const listFiles = async ( options?: RequestInit): Promise<FileList> => {
  
  return customFetch<FileList>(getListFilesUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListFilesQueryKey = () => {
    return [
    `/api/files`
    ] as const;
    }

    
export const getListFilesQueryOptions = <TData = Awaited<ReturnType<typeof listFiles>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListFilesQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listFiles>>> = ({ signal }) => listFiles({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData> & { queryKey: QueryKey }
}

export type ListFilesQueryResult = NonNullable<Awaited<ReturnType<typeof listFiles>>>
export type ListFilesQueryError = Error


/**
 * @summary List stored files
 */

export function useListFiles<TData = Awaited<ReturnType<typeof listFiles>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListFilesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Upload a file
 */
export const getUploadFileUrl = () => {


  

  return `/api/files`
}

export const uploadFile = async (uploadFileBody: UploadFileBody, options?: RequestInit): Promise<StoredFile> => {
    const formData = new FormData();
formData.append(`file`, uploadFileBody.file)

  return customFetch<StoredFile>(getUploadFileUrl(),
  {      
    ...options,
    method: 'POST'
    ,
    body: 
      formData,
  }
);}




export const getUploadFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadFile>>, TError,{data: UploadFileBody}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadFile>>, TError,{data: UploadFileBody}, TContext> => {

const mutationKey = ['uploadFile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadFile>>, {data: UploadFileBody}> = (props) => {
          const {data} = props ?? {};

          return  uploadFile(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UploadFileMutationResult = NonNullable<Awaited<ReturnType<typeof uploadFile>>>
    export type UploadFileMutationBody = UploadFileBody
    export type UploadFileMutationError = Error

    /**
 * @summary Upload a file
 */
export const useUploadFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadFile>>, TError,{data: UploadFileBody}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadFile>>,
        TError,
        {data: UploadFileBody},
        TContext
      > => {

      const mutationOptions = getUploadFileMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Delete a stored file
 */
export const getDeleteFileUrl = (id: string,) => {


  

  return `/api/files/${id}`
}

export const deleteFile = async (id: string, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getDeleteFileUrl(id),
  {      
    ...options,
    method: 'DELETE'
    
    
  }
);}




export const getDeleteFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteFile>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteFile>>, TError,{id: string}, TContext> => {

const mutationKey = ['deleteFile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteFile>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  deleteFile(id,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteFileMutationResult = NonNullable<Awaited<ReturnType<typeof deleteFile>>>
    
    export type DeleteFileMutationError = Error

    /**
 * @summary Delete a stored file
 */
export const useDeleteFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteFile>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteFile>>,
        TError,
        {id: string},
        TContext
      > => {

      const mutationOptions = getDeleteFileMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Download file content
 */
export const getDownloadFileUrl = (id: string,) => {


  

  return `/api/files/${id}/content`
}

export const downloadFile = async (id: string, options?: RequestInit): Promise<Blob> => {
  
  return customFetch<Blob>(getDownloadFileUrl(id),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getDownloadFileQueryKey = (id?: string,) => {
    return [
    `/api/files/${id}/content`
    ] as const;
    }

    
export const getDownloadFileQueryOptions = <TData = Awaited<ReturnType<typeof downloadFile>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadFileQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadFile>>> = ({ signal }) => downloadFile(id, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadFileQueryResult = NonNullable<Awaited<ReturnType<typeof downloadFile>>>
export type DownloadFileQueryError = Error


/**
 * @summary Download file content
 */

export function useDownloadFile<TData = Awaited<ReturnType<typeof downloadFile>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadFileQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Receives an inbound SMS from a parent. The sender phone is normalised
to E.164 and matched against the `guardians` table.  Validated messages
are logged against the matching student(s) and the parsed dismissal
preference is surfaced on the live event workspace.

 * @summary Inbound SMS webhook (Twilio / MessageMedia)
 */
export const getReceiveSmsUrl = () => {


  

  return `/api/sms/incoming`
}

export const receiveSms = async (smsIncoming: SmsIncoming, options?: RequestInit): Promise<SmsLog> => {
  
  return customFetch<SmsLog>(getReceiveSmsUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      smsIncoming,)
  }
);}




export const getReceiveSmsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveSms>>, TError,{data: SmsIncoming}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof receiveSms>>, TError,{data: SmsIncoming}, TContext> => {

const mutationKey = ['receiveSms'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof receiveSms>>, {data: SmsIncoming}> = (props) => {
          const {data} = props ?? {};

          return  receiveSms(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type ReceiveSmsMutationResult = NonNullable<Awaited<ReturnType<typeof receiveSms>>>
    export type ReceiveSmsMutationBody = SmsIncoming
    export type ReceiveSmsMutationError = Error

    /**
 * @summary Inbound SMS webhook (Twilio / MessageMedia)
 */
export const useReceiveSms = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveSms>>, TError,{data: SmsIncoming}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof receiveSms>>,
        TError,
        {data: SmsIncoming},
        TContext
      > => {

      const mutationOptions = getReceiveSmsMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Voice behavior config for the mobile register (defaults apply when unset).
 * @summary Get attendance register settings
 */
export const getGetAttendanceSettingsUrl = () => {


  

  return `/api/settings/attendance`
}

export const getAttendanceSettings = async ( options?: RequestInit): Promise<AttendanceSettings> => {
  
  return customFetch<AttendanceSettings>(getGetAttendanceSettingsUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetAttendanceSettingsQueryKey = () => {
    return [
    `/api/settings/attendance`
    ] as const;
    }

    
export const getGetAttendanceSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getAttendanceSettings>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAttendanceSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAttendanceSettingsQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAttendanceSettings>>> = ({ signal }) => getAttendanceSettings({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAttendanceSettings>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAttendanceSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getAttendanceSettings>>>
export type GetAttendanceSettingsQueryError = unknown


/**
 * @summary Get attendance register settings
 */

export function useGetAttendanceSettings<TData = Awaited<ReturnType<typeof getAttendanceSettings>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAttendanceSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAttendanceSettingsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * Admin-only when authentication is enabled. Persists voice behavior config.
 * @summary Update attendance register settings
 */
export const getPutAttendanceSettingsUrl = () => {


  

  return `/api/settings/attendance`
}

export const putAttendanceSettings = async (attendanceSettings: AttendanceSettings, options?: RequestInit): Promise<AttendanceSettings> => {
  
  return customFetch<AttendanceSettings>(getPutAttendanceSettingsUrl(),
  {      
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      attendanceSettings,)
  }
);}




export const getPutAttendanceSettingsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putAttendanceSettings>>, TError,{data: AttendanceSettings}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putAttendanceSettings>>, TError,{data: AttendanceSettings}, TContext> => {

const mutationKey = ['putAttendanceSettings'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putAttendanceSettings>>, {data: AttendanceSettings}> = (props) => {
          const {data} = props ?? {};

          return  putAttendanceSettings(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type PutAttendanceSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof putAttendanceSettings>>>
    export type PutAttendanceSettingsMutationBody = AttendanceSettings
    export type PutAttendanceSettingsMutationError = Error

    /**
 * @summary Update attendance register settings
 */
export const usePutAttendanceSettings = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putAttendanceSettings>>, TError,{data: AttendanceSettings}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof putAttendanceSettings>>,
        TError,
        {data: AttendanceSettings},
        TContext
      > => {

      const mutationOptions = getPutAttendanceSettingsMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary List events
 */
export const getListEventsUrl = (params?: ListEventsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/events?${stringifiedParams}` : `/api/events`
}

export const listEvents = async (params?: ListEventsParams, options?: RequestInit): Promise<EventList> => {
  
  return customFetch<EventList>(getListEventsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListEventsQueryKey = (params?: ListEventsParams,) => {
    return [
    `/api/events`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getListEventsQueryOptions = <TData = Awaited<ReturnType<typeof listEvents>>, TError = unknown>(params?: ListEventsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEvents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEventsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEvents>>> = ({ signal }) => listEvents(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEvents>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEventsQueryResult = NonNullable<Awaited<ReturnType<typeof listEvents>>>
export type ListEventsQueryError = unknown


/**
 * @summary List events
 */

export function useListEvents<TData = Awaited<ReturnType<typeof listEvents>>, TError = unknown>(
 params?: ListEventsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEvents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEventsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Create event (or recurring series) and check in students
 */
export const getCreateEventUrl = () => {


  

  return `/api/events`
}

export const createEvent = async (eventCreate: EventCreate, options?: RequestInit): Promise<EventList> => {
  
  return customFetch<EventList>(getCreateEventUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      eventCreate,)
  }
);}




export const getCreateEventMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEvent>>, TError,{data: EventCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEvent>>, TError,{data: EventCreate}, TContext> => {

const mutationKey = ['createEvent'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEvent>>, {data: EventCreate}> = (props) => {
          const {data} = props ?? {};

          return  createEvent(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateEventMutationResult = NonNullable<Awaited<ReturnType<typeof createEvent>>>
    export type CreateEventMutationBody = EventCreate
    export type CreateEventMutationError = Error

    /**
 * @summary Create event (or recurring series) and check in students
 */
export const useCreateEvent = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEvent>>, TError,{data: EventCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createEvent>>,
        TError,
        {data: EventCreate},
        TContext
      > => {

      const mutationOptions = getCreateEventMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Get event details
 */
export const getGetEventUrl = (id: string,) => {


  

  return `/api/events/${id}`
}

export const getEvent = async (id: string, options?: RequestInit): Promise<Event> => {
  
  return customFetch<Event>(getGetEventUrl(id),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetEventQueryKey = (id?: string,) => {
    return [
    `/api/events/${id}`
    ] as const;
    }

    
export const getGetEventQueryOptions = <TData = Awaited<ReturnType<typeof getEvent>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEvent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEventQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEvent>>> = ({ signal }) => getEvent(id, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEvent>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEventQueryResult = NonNullable<Awaited<ReturnType<typeof getEvent>>>
export type GetEventQueryError = Error


/**
 * @summary Get event details
 */

export function useGetEvent<TData = Awaited<ReturnType<typeof getEvent>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEvent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEventQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Update event (e.g. mark completed)
 */
export const getUpdateEventUrl = (id: string,) => {


  

  return `/api/events/${id}`
}

export const updateEvent = async (id: string,
    eventUpdate: EventUpdate, options?: RequestInit): Promise<Event> => {
  
  return customFetch<Event>(getUpdateEventUrl(id),
  {      
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      eventUpdate,)
  }
);}




export const getUpdateEventMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEvent>>, TError,{id: string;data: EventUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEvent>>, TError,{id: string;data: EventUpdate}, TContext> => {

const mutationKey = ['updateEvent'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEvent>>, {id: string;data: EventUpdate}> = (props) => {
          const {id,data} = props ?? {};

          return  updateEvent(id,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateEventMutationResult = NonNullable<Awaited<ReturnType<typeof updateEvent>>>
    export type UpdateEventMutationBody = EventUpdate
    export type UpdateEventMutationError = Error

    /**
 * @summary Update event (e.g. mark completed)
 */
export const useUpdateEvent = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEvent>>, TError,{id: string;data: EventUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateEvent>>,
        TError,
        {id: string;data: EventUpdate},
        TContext
      > => {

      const mutationOptions = getUpdateEventMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Removes the event. Attendance rows are cascade-deleted; SMS logs and volunteer links are set to null. Requires admin or teacher role.

 * @summary Delete an event and its attendance
 */
export const getDeleteEventUrl = (id: string,) => {


  

  return `/api/events/${id}`
}

export const deleteEvent = async (id: string, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getDeleteEventUrl(id),
  {      
    ...options,
    method: 'DELETE'
    
    
  }
);}




export const getDeleteEventMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEvent>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteEvent>>, TError,{id: string}, TContext> => {

const mutationKey = ['deleteEvent'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteEvent>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  deleteEvent(id,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteEventMutationResult = NonNullable<Awaited<ReturnType<typeof deleteEvent>>>
    
    export type DeleteEventMutationError = Error

    /**
 * @summary Delete an event and its attendance
 */
export const useDeleteEvent = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEvent>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteEvent>>,
        TError,
        {id: string},
        TContext
      > => {

      const mutationOptions = getDeleteEventMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * Returns all attendance records for the event, each enriched with the
student's details, their guardians, the latest SMS-derived preference,
and the current check-in / sign-out state.

 * @summary Get live attendance for an event
 */
export const getGetEventAttendanceUrl = (id: string,) => {


  

  return `/api/events/${id}/attendance`
}

export const getEventAttendance = async (id: string, options?: RequestInit): Promise<AttendanceList> => {
  
  return customFetch<AttendanceList>(getGetEventAttendanceUrl(id),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetEventAttendanceQueryKey = (id?: string,) => {
    return [
    `/api/events/${id}/attendance`
    ] as const;
    }

    
export const getGetEventAttendanceQueryOptions = <TData = Awaited<ReturnType<typeof getEventAttendance>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEventAttendance>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEventAttendanceQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEventAttendance>>> = ({ signal }) => getEventAttendance(id, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEventAttendance>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEventAttendanceQueryResult = NonNullable<Awaited<ReturnType<typeof getEventAttendance>>>
export type GetEventAttendanceQueryError = Error


/**
 * @summary Get live attendance for an event
 */

export function useGetEventAttendance<TData = Awaited<ReturnType<typeof getEventAttendance>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEventAttendance>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEventAttendanceQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Sign out / update an attendance record
 */
export const getUpdateAttendanceUrl = (id: string,) => {


  

  return `/api/attendance/${id}`
}

export const updateAttendance = async (id: string,
    attendanceUpdate: AttendanceUpdate, options?: RequestInit): Promise<AttendanceRecord> => {
  
  return customFetch<AttendanceRecord>(getUpdateAttendanceUrl(id),
  {      
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      attendanceUpdate,)
  }
);}




export const getUpdateAttendanceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAttendance>>, TError,{id: string;data: AttendanceUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAttendance>>, TError,{id: string;data: AttendanceUpdate}, TContext> => {

const mutationKey = ['updateAttendance'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAttendance>>, {id: string;data: AttendanceUpdate}> = (props) => {
          const {id,data} = props ?? {};

          return  updateAttendance(id,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateAttendanceMutationResult = NonNullable<Awaited<ReturnType<typeof updateAttendance>>>
    export type UpdateAttendanceMutationBody = AttendanceUpdate
    export type UpdateAttendanceMutationError = Error

    /**
 * @summary Sign out / update an attendance record
 */
export const useUpdateAttendance = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAttendance>>, TError,{id: string;data: AttendanceUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAttendance>>,
        TError,
        {id: string;data: AttendanceUpdate},
        TContext
      > => {

      const mutationOptions = getUpdateAttendanceMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary List roll calls for an event
 */
export const getListRollCallsUrl = (id: string,) => {


  

  return `/api/events/${id}/roll-calls`
}

export const listRollCalls = async (id: string, options?: RequestInit): Promise<RollCallList> => {
  
  return customFetch<RollCallList>(getListRollCallsUrl(id),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListRollCallsQueryKey = (id?: string,) => {
    return [
    `/api/events/${id}/roll-calls`
    ] as const;
    }

    
export const getListRollCallsQueryOptions = <TData = Awaited<ReturnType<typeof listRollCalls>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRollCalls>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRollCallsQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRollCalls>>> = ({ signal }) => listRollCalls(id, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRollCalls>>, TError, TData> & { queryKey: QueryKey }
}

export type ListRollCallsQueryResult = NonNullable<Awaited<ReturnType<typeof listRollCalls>>>
export type ListRollCallsQueryError = unknown


/**
 * @summary List roll calls for an event
 */

export function useListRollCalls<TData = Awaited<ReturnType<typeof listRollCalls>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRollCalls>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListRollCallsQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Create a new roll call for an event
 */
export const getCreateRollCallUrl = (id: string,) => {


  

  return `/api/events/${id}/roll-calls`
}

export const createRollCall = async (id: string,
    rollCallCreate: RollCallCreate, options?: RequestInit): Promise<RollCall> => {
  
  return customFetch<RollCall>(getCreateRollCallUrl(id),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      rollCallCreate,)
  }
);}




export const getCreateRollCallMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createRollCall>>, TError,{id: string;data: RollCallCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createRollCall>>, TError,{id: string;data: RollCallCreate}, TContext> => {

const mutationKey = ['createRollCall'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createRollCall>>, {id: string;data: RollCallCreate}> = (props) => {
          const {id,data} = props ?? {};

          return  createRollCall(id,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateRollCallMutationResult = NonNullable<Awaited<ReturnType<typeof createRollCall>>>
    export type CreateRollCallMutationBody = RollCallCreate
    export type CreateRollCallMutationError = unknown

    /**
 * @summary Create a new roll call for an event
 */
export const useCreateRollCall = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createRollCall>>, TError,{id: string;data: RollCallCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createRollCall>>,
        TError,
        {id: string;data: RollCallCreate},
        TContext
      > => {

      const mutationOptions = getCreateRollCallMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Rename a roll call
 */
export const getUpdateRollCallUrl = (id: string,) => {


  

  return `/api/roll-calls/${id}`
}

export const updateRollCall = async (id: string,
    rollCallUpdate: RollCallUpdate, options?: RequestInit): Promise<RollCall> => {
  
  return customFetch<RollCall>(getUpdateRollCallUrl(id),
  {      
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      rollCallUpdate,)
  }
);}




export const getUpdateRollCallMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateRollCall>>, TError,{id: string;data: RollCallUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateRollCall>>, TError,{id: string;data: RollCallUpdate}, TContext> => {

const mutationKey = ['updateRollCall'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateRollCall>>, {id: string;data: RollCallUpdate}> = (props) => {
          const {id,data} = props ?? {};

          return  updateRollCall(id,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateRollCallMutationResult = NonNullable<Awaited<ReturnType<typeof updateRollCall>>>
    export type UpdateRollCallMutationBody = RollCallUpdate
    export type UpdateRollCallMutationError = Error

    /**
 * @summary Rename a roll call
 */
export const useUpdateRollCall = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateRollCall>>, TError,{id: string;data: RollCallUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateRollCall>>,
        TError,
        {id: string;data: RollCallUpdate},
        TContext
      > => {

      const mutationOptions = getUpdateRollCallMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Delete a roll call
 */
export const getDeleteRollCallUrl = (id: string,) => {


  

  return `/api/roll-calls/${id}`
}

export const deleteRollCall = async (id: string, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getDeleteRollCallUrl(id),
  {      
    ...options,
    method: 'DELETE'
    
    
  }
);}




export const getDeleteRollCallMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteRollCall>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteRollCall>>, TError,{id: string}, TContext> => {

const mutationKey = ['deleteRollCall'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteRollCall>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  deleteRollCall(id,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteRollCallMutationResult = NonNullable<Awaited<ReturnType<typeof deleteRollCall>>>
    
    export type DeleteRollCallMutationError = Error

    /**
 * @summary Delete a roll call
 */
export const useDeleteRollCall = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteRollCall>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteRollCall>>,
        TError,
        {id: string},
        TContext
      > => {

      const mutationOptions = getDeleteRollCallMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary List entries (per-student marks) for a roll call
 */
export const getGetRollCallEntriesUrl = (id: string,) => {


  

  return `/api/roll-calls/${id}/entries`
}

export const getRollCallEntries = async (id: string, options?: RequestInit): Promise<RollCallEntryList> => {
  
  return customFetch<RollCallEntryList>(getGetRollCallEntriesUrl(id),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetRollCallEntriesQueryKey = (id?: string,) => {
    return [
    `/api/roll-calls/${id}/entries`
    ] as const;
    }

    
export const getGetRollCallEntriesQueryOptions = <TData = Awaited<ReturnType<typeof getRollCallEntries>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRollCallEntries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRollCallEntriesQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRollCallEntries>>> = ({ signal }) => getRollCallEntries(id, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getRollCallEntries>>, TError, TData> & { queryKey: QueryKey }
}

export type GetRollCallEntriesQueryResult = NonNullable<Awaited<ReturnType<typeof getRollCallEntries>>>
export type GetRollCallEntriesQueryError = Error


/**
 * @summary List entries (per-student marks) for a roll call
 */

export function useGetRollCallEntries<TData = Awaited<ReturnType<typeof getRollCallEntries>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRollCallEntries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetRollCallEntriesQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Mark a student present / absent / pending in a roll call
 */
export const getUpdateRollCallEntryUrl = (id: string,) => {


  

  return `/api/roll-call-entries/${id}`
}

export const updateRollCallEntry = async (id: string,
    rollCallEntryUpdate: RollCallEntryUpdate, options?: RequestInit): Promise<RollCallEntry> => {
  
  return customFetch<RollCallEntry>(getUpdateRollCallEntryUrl(id),
  {      
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      rollCallEntryUpdate,)
  }
);}




export const getUpdateRollCallEntryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateRollCallEntry>>, TError,{id: string;data: RollCallEntryUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateRollCallEntry>>, TError,{id: string;data: RollCallEntryUpdate}, TContext> => {

const mutationKey = ['updateRollCallEntry'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateRollCallEntry>>, {id: string;data: RollCallEntryUpdate}> = (props) => {
          const {id,data} = props ?? {};

          return  updateRollCallEntry(id,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateRollCallEntryMutationResult = NonNullable<Awaited<ReturnType<typeof updateRollCallEntry>>>
    export type UpdateRollCallEntryMutationBody = RollCallEntryUpdate
    export type UpdateRollCallEntryMutationError = Error

    /**
 * @summary Mark a student present / absent / pending in a roll call
 */
export const useUpdateRollCallEntry = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateRollCallEntry>>, TError,{id: string;data: RollCallEntryUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateRollCallEntry>>,
        TError,
        {id: string;data: RollCallEntryUpdate},
        TContext
      > => {

      const mutationOptions = getUpdateRollCallEntryMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary List students (with guardians)
 */
export const getListStudentsUrl = (params?: ListStudentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/students?${stringifiedParams}` : `/api/students`
}

export const listStudents = async (params?: ListStudentsParams, options?: RequestInit): Promise<StudentList> => {
  
  return customFetch<StudentList>(getListStudentsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListStudentsQueryKey = (params?: ListStudentsParams,) => {
    return [
    `/api/students`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getListStudentsQueryOptions = <TData = Awaited<ReturnType<typeof listStudents>>, TError = unknown>(params?: ListStudentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listStudents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListStudentsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listStudents>>> = ({ signal }) => listStudents(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listStudents>>, TError, TData> & { queryKey: QueryKey }
}

export type ListStudentsQueryResult = NonNullable<Awaited<ReturnType<typeof listStudents>>>
export type ListStudentsQueryError = unknown


/**
 * @summary List students (with guardians)
 */

export function useListStudents<TData = Awaited<ReturnType<typeof listStudents>>, TError = unknown>(
 params?: ListStudentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listStudents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListStudentsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Create student (optionally with a guardian)
 */
export const getCreateStudentUrl = () => {


  

  return `/api/students`
}

export const createStudent = async (studentCreate: StudentCreate, options?: RequestInit): Promise<Student> => {
  
  return customFetch<Student>(getCreateStudentUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      studentCreate,)
  }
);}




export const getCreateStudentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createStudent>>, TError,{data: StudentCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createStudent>>, TError,{data: StudentCreate}, TContext> => {

const mutationKey = ['createStudent'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createStudent>>, {data: StudentCreate}> = (props) => {
          const {data} = props ?? {};

          return  createStudent(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateStudentMutationResult = NonNullable<Awaited<ReturnType<typeof createStudent>>>
    export type CreateStudentMutationBody = StudentCreate
    export type CreateStudentMutationError = Error

    /**
 * @summary Create student (optionally with a guardian)
 */
export const useCreateStudent = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createStudent>>, TError,{data: StudentCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createStudent>>,
        TError,
        {data: StudentCreate},
        TContext
      > => {

      const mutationOptions = getCreateStudentMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Bulk import students from a CSV spreadsheet (with duplicate detection)
 */
export const getImportStudentsUrl = () => {


  

  return `/api/students/import`
}

export const importStudents = async (importStudentsBody: ImportStudentsBody, options?: RequestInit): Promise<StudentImportResult> => {
    const formData = new FormData();
formData.append(`file`, importStudentsBody.file)

  return customFetch<StudentImportResult>(getImportStudentsUrl(),
  {      
    ...options,
    method: 'POST'
    ,
    body: 
      formData,
  }
);}




export const getImportStudentsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importStudents>>, TError,{data: ImportStudentsBody}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof importStudents>>, TError,{data: ImportStudentsBody}, TContext> => {

const mutationKey = ['importStudents'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importStudents>>, {data: ImportStudentsBody}> = (props) => {
          const {data} = props ?? {};

          return  importStudents(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type ImportStudentsMutationResult = NonNullable<Awaited<ReturnType<typeof importStudents>>>
    export type ImportStudentsMutationBody = ImportStudentsBody
    export type ImportStudentsMutationError = Error

    /**
 * @summary Bulk import students from a CSV spreadsheet (with duplicate detection)
 */
export const useImportStudents = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importStudents>>, TError,{data: ImportStudentsBody}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof importStudents>>,
        TError,
        {data: ImportStudentsBody},
        TContext
      > => {

      const mutationOptions = getImportStudentsMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Update student details
 */
export const getUpdateStudentUrl = (id: string,) => {


  

  return `/api/students/${id}`
}

export const updateStudent = async (id: string,
    studentUpdate: StudentUpdate, options?: RequestInit): Promise<Student> => {
  
  return customFetch<Student>(getUpdateStudentUrl(id),
  {      
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      studentUpdate,)
  }
);}




export const getUpdateStudentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateStudent>>, TError,{id: string;data: StudentUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateStudent>>, TError,{id: string;data: StudentUpdate}, TContext> => {

const mutationKey = ['updateStudent'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateStudent>>, {id: string;data: StudentUpdate}> = (props) => {
          const {id,data} = props ?? {};

          return  updateStudent(id,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateStudentMutationResult = NonNullable<Awaited<ReturnType<typeof updateStudent>>>
    export type UpdateStudentMutationBody = StudentUpdate
    export type UpdateStudentMutationError = Error

    /**
 * @summary Update student details
 */
export const useUpdateStudent = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateStudent>>, TError,{id: string;data: StudentUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateStudent>>,
        TError,
        {id: string;data: StudentUpdate},
        TContext
      > => {

      const mutationOptions = getUpdateStudentMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Update guardian (name, phone)
 */
export const getUpdateGuardianUrl = (id: string,) => {


  

  return `/api/guardians/${id}`
}

export const updateGuardian = async (id: string,
    guardianUpdate: GuardianUpdate, options?: RequestInit): Promise<Guardian> => {
  
  return customFetch<Guardian>(getUpdateGuardianUrl(id),
  {      
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      guardianUpdate,)
  }
);}




export const getUpdateGuardianMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateGuardian>>, TError,{id: string;data: GuardianUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateGuardian>>, TError,{id: string;data: GuardianUpdate}, TContext> => {

const mutationKey = ['updateGuardian'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateGuardian>>, {id: string;data: GuardianUpdate}> = (props) => {
          const {id,data} = props ?? {};

          return  updateGuardian(id,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateGuardianMutationResult = NonNullable<Awaited<ReturnType<typeof updateGuardian>>>
    export type UpdateGuardianMutationBody = GuardianUpdate
    export type UpdateGuardianMutationError = Error

    /**
 * @summary Update guardian (name, phone)
 */
export const useUpdateGuardian = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateGuardian>>, TError,{id: string;data: GuardianUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateGuardian>>,
        TError,
        {id: string;data: GuardianUpdate},
        TContext
      > => {

      const mutationOptions = getUpdateGuardianMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
export const getDeleteGuardianUrl = (id: string,) => {


  

  return `/api/guardians/${id}`
}

export const deleteGuardian = async (id: string, options?: RequestInit): Promise<void> => {
  
  return customFetch<void>(getDeleteGuardianUrl(id),
  {      
    ...options,
    method: 'DELETE'
    
    
  }
);}




export const getDeleteGuardianMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteGuardian>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteGuardian>>, TError,{id: string}, TContext> => {

const mutationKey = ['deleteGuardian'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteGuardian>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  deleteGuardian(id,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteGuardianMutationResult = NonNullable<Awaited<ReturnType<typeof deleteGuardian>>>
    
    export type DeleteGuardianMutationError = Error

    export const useDeleteGuardian = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteGuardian>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteGuardian>>,
        TError,
        {id: string},
        TContext
      > => {

      const mutationOptions = getDeleteGuardianMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary SMS history for a student
 */
export const getGetStudentSmsLogsUrl = (id: string,) => {


  

  return `/api/students/${id}/sms-logs`
}

export const getStudentSmsLogs = async (id: string, options?: RequestInit): Promise<SmsLogList> => {
  
  return customFetch<SmsLogList>(getGetStudentSmsLogsUrl(id),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getGetStudentSmsLogsQueryKey = (id?: string,) => {
    return [
    `/api/students/${id}/sms-logs`
    ] as const;
    }

    
export const getGetStudentSmsLogsQueryOptions = <TData = Awaited<ReturnType<typeof getStudentSmsLogs>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getStudentSmsLogs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetStudentSmsLogsQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getStudentSmsLogs>>> = ({ signal }) => getStudentSmsLogs(id, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id), ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getStudentSmsLogs>>, TError, TData> & { queryKey: QueryKey }
}

export type GetStudentSmsLogsQueryResult = NonNullable<Awaited<ReturnType<typeof getStudentSmsLogs>>>
export type GetStudentSmsLogsQueryError = Error


/**
 * @summary SMS history for a student
 */

export function useGetStudentSmsLogs<TData = Awaited<ReturnType<typeof getStudentSmsLogs>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getStudentSmsLogs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetStudentSmsLogsQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary List volunteers
 */
export const getListVolunteersUrl = (params?: ListVolunteersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/volunteers?${stringifiedParams}` : `/api/volunteers`
}

export const listVolunteers = async (params?: ListVolunteersParams, options?: RequestInit): Promise<VolunteerList> => {
  
  return customFetch<VolunteerList>(getListVolunteersUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListVolunteersQueryKey = (params?: ListVolunteersParams,) => {
    return [
    `/api/volunteers`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getListVolunteersQueryOptions = <TData = Awaited<ReturnType<typeof listVolunteers>>, TError = unknown>(params?: ListVolunteersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listVolunteers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListVolunteersQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listVolunteers>>> = ({ signal }) => listVolunteers(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listVolunteers>>, TError, TData> & { queryKey: QueryKey }
}

export type ListVolunteersQueryResult = NonNullable<Awaited<ReturnType<typeof listVolunteers>>>
export type ListVolunteersQueryError = unknown


/**
 * @summary List volunteers
 */

export function useListVolunteers<TData = Awaited<ReturnType<typeof listVolunteers>>, TError = unknown>(
 params?: ListVolunteersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listVolunteers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListVolunteersQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Create volunteer
 */
export const getCreateVolunteerUrl = () => {


  

  return `/api/volunteers`
}

export const createVolunteer = async (volunteerCreate: VolunteerCreate, options?: RequestInit): Promise<Volunteer> => {
  
  return customFetch<Volunteer>(getCreateVolunteerUrl(),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      volunteerCreate,)
  }
);}




export const getCreateVolunteerMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVolunteer>>, TError,{data: VolunteerCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createVolunteer>>, TError,{data: VolunteerCreate}, TContext> => {

const mutationKey = ['createVolunteer'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createVolunteer>>, {data: VolunteerCreate}> = (props) => {
          const {data} = props ?? {};

          return  createVolunteer(data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateVolunteerMutationResult = NonNullable<Awaited<ReturnType<typeof createVolunteer>>>
    export type CreateVolunteerMutationBody = VolunteerCreate
    export type CreateVolunteerMutationError = Error

    /**
 * @summary Create volunteer
 */
export const useCreateVolunteer = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVolunteer>>, TError,{data: VolunteerCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createVolunteer>>,
        TError,
        {data: VolunteerCreate},
        TContext
      > => {

      const mutationOptions = getCreateVolunteerMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Update volunteer (WWCC, induction)
 */
export const getUpdateVolunteerUrl = (id: string,) => {


  

  return `/api/volunteers/${id}`
}

export const updateVolunteer = async (id: string,
    volunteerUpdate: VolunteerUpdate, options?: RequestInit): Promise<Volunteer> => {
  
  return customFetch<Volunteer>(getUpdateVolunteerUrl(id),
  {      
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      volunteerUpdate,)
  }
);}




export const getUpdateVolunteerMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateVolunteer>>, TError,{id: string;data: VolunteerUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateVolunteer>>, TError,{id: string;data: VolunteerUpdate}, TContext> => {

const mutationKey = ['updateVolunteer'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateVolunteer>>, {id: string;data: VolunteerUpdate}> = (props) => {
          const {id,data} = props ?? {};

          return  updateVolunteer(id,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateVolunteerMutationResult = NonNullable<Awaited<ReturnType<typeof updateVolunteer>>>
    export type UpdateVolunteerMutationBody = VolunteerUpdate
    export type UpdateVolunteerMutationError = Error

    /**
 * @summary Update volunteer (WWCC, induction)
 */
export const useUpdateVolunteer = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateVolunteer>>, TError,{id: string;data: VolunteerUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateVolunteer>>,
        TError,
        {id: string;data: VolunteerUpdate},
        TContext
      > => {

      const mutationOptions = getUpdateVolunteerMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Audit log of sign-out decisions and SMS trail
 */
export const getListAuditSignoutsUrl = (params?: ListAuditSignoutsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/audit/signouts?${stringifiedParams}` : `/api/audit/signouts`
}

export const listAuditSignouts = async (params?: ListAuditSignoutsParams, options?: RequestInit): Promise<AuditList> => {
  
  return customFetch<AuditList>(getListAuditSignoutsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListAuditSignoutsQueryKey = (params?: ListAuditSignoutsParams,) => {
    return [
    `/api/audit/signouts`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getListAuditSignoutsQueryOptions = <TData = Awaited<ReturnType<typeof listAuditSignouts>>, TError = unknown>(params?: ListAuditSignoutsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAuditSignouts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAuditSignoutsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAuditSignouts>>> = ({ signal }) => listAuditSignouts(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAuditSignouts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAuditSignoutsQueryResult = NonNullable<Awaited<ReturnType<typeof listAuditSignouts>>>
export type ListAuditSignoutsQueryError = unknown


/**
 * @summary Audit log of sign-out decisions and SMS trail
 */

export function useListAuditSignouts<TData = Awaited<ReturnType<typeof listAuditSignouts>>, TError = unknown>(
 params?: ListAuditSignoutsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAuditSignouts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAuditSignoutsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary List phone approval requests (admin only)
 */
export const getListPhoneApprovalsUrl = (params?: ListPhoneApprovalsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString())
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/phone-approvals?${stringifiedParams}` : `/api/phone-approvals`
}

export const listPhoneApprovals = async (params?: ListPhoneApprovalsParams, options?: RequestInit): Promise<PhoneApprovalList> => {
  
  return customFetch<PhoneApprovalList>(getListPhoneApprovalsUrl(params),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListPhoneApprovalsQueryKey = (params?: ListPhoneApprovalsParams,) => {
    return [
    `/api/phone-approvals`, ...(params ? [params]: [])
    ] as const;
    }

    
export const getListPhoneApprovalsQueryOptions = <TData = Awaited<ReturnType<typeof listPhoneApprovals>>, TError = Error>(params?: ListPhoneApprovalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPhoneApprovals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPhoneApprovalsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPhoneApprovals>>> = ({ signal }) => listPhoneApprovals(params, { signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPhoneApprovals>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPhoneApprovalsQueryResult = NonNullable<Awaited<ReturnType<typeof listPhoneApprovals>>>
export type ListPhoneApprovalsQueryError = Error


/**
 * @summary List phone approval requests (admin only)
 */

export function useListPhoneApprovals<TData = Awaited<ReturnType<typeof listPhoneApprovals>>, TError = Error>(
 params?: ListPhoneApprovalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPhoneApprovals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPhoneApprovalsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





/**
 * @summary Approve a pending phone number (admin only)
 */
export const getApprovePhoneNumberUrl = (id: string,) => {


  

  return `/api/phone-approvals/${id}/approve`
}

export const approvePhoneNumber = async (id: string,
    approvePhoneBody: ApprovePhoneBody, options?: RequestInit): Promise<ApprovePhoneResult> => {
  
  return customFetch<ApprovePhoneResult>(getApprovePhoneNumberUrl(id),
  {      
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(
      approvePhoneBody,)
  }
);}




export const getApprovePhoneNumberMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approvePhoneNumber>>, TError,{id: string;data: ApprovePhoneBody}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof approvePhoneNumber>>, TError,{id: string;data: ApprovePhoneBody}, TContext> => {

const mutationKey = ['approvePhoneNumber'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof approvePhoneNumber>>, {id: string;data: ApprovePhoneBody}> = (props) => {
          const {id,data} = props ?? {};

          return  approvePhoneNumber(id,data,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type ApprovePhoneNumberMutationResult = NonNullable<Awaited<ReturnType<typeof approvePhoneNumber>>>
    export type ApprovePhoneNumberMutationBody = ApprovePhoneBody
    export type ApprovePhoneNumberMutationError = Error

    /**
 * @summary Approve a pending phone number (admin only)
 */
export const useApprovePhoneNumber = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approvePhoneNumber>>, TError,{id: string;data: ApprovePhoneBody}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof approvePhoneNumber>>,
        TError,
        {id: string;data: ApprovePhoneBody},
        TContext
      > => {

      const mutationOptions = getApprovePhoneNumberMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
/**
 * @summary Reject a pending phone number (admin only)
 */
export const getRejectPhoneNumberUrl = (id: string,) => {


  

  return `/api/phone-approvals/${id}/reject`
}

export const rejectPhoneNumber = async (id: string, options?: RequestInit): Promise<PhoneApprovalRequest> => {
  
  return customFetch<PhoneApprovalRequest>(getRejectPhoneNumberUrl(id),
  {      
    ...options,
    method: 'POST'
    
    
  }
);}




export const getRejectPhoneNumberMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectPhoneNumber>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof rejectPhoneNumber>>, TError,{id: string}, TContext> => {

const mutationKey = ['rejectPhoneNumber'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof rejectPhoneNumber>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  rejectPhoneNumber(id,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type RejectPhoneNumberMutationResult = NonNullable<Awaited<ReturnType<typeof rejectPhoneNumber>>>
    
    export type RejectPhoneNumberMutationError = Error

    /**
 * @summary Reject a pending phone number (admin only)
 */
export const useRejectPhoneNumber = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectPhoneNumber>>, TError,{id: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof rejectPhoneNumber>>,
        TError,
        {id: string},
        TContext
      > => {

      const mutationOptions = getRejectPhoneNumberMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
export const getListApprovedNumbersUrl = () => {


  

  return `/api/approved-numbers`
}

export const listApprovedNumbers = async ( options?: RequestInit): Promise<ApprovedNumberList> => {
  
  return customFetch<ApprovedNumberList>(getListApprovedNumbersUrl(),
  {      
    ...options,
    method: 'GET'
    
    
  }
);}





export const getListApprovedNumbersQueryKey = () => {
    return [
    `/api/approved-numbers`
    ] as const;
    }

    
export const getListApprovedNumbersQueryOptions = <TData = Awaited<ReturnType<typeof listApprovedNumbers>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listApprovedNumbers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListApprovedNumbersQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof listApprovedNumbers>>> = ({ signal }) => listApprovedNumbers({ signal, ...requestOptions });

      

      

   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listApprovedNumbers>>, TError, TData> & { queryKey: QueryKey }
}

export type ListApprovedNumbersQueryResult = NonNullable<Awaited<ReturnType<typeof listApprovedNumbers>>>
export type ListApprovedNumbersQueryError = Error



export function useListApprovedNumbers<TData = Awaited<ReturnType<typeof listApprovedNumbers>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listApprovedNumbers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
  
 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListApprovedNumbersQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}





export const getLinkGuardianStudentUrl = (id: string,
    studentId: string,) => {


  

  return `/api/guardians/${id}/students/${studentId}`
}

export const linkGuardianStudent = async (id: string,
    studentId: string, options?: RequestInit): Promise<Guardian> => {
  
  return customFetch<Guardian>(getLinkGuardianStudentUrl(id,studentId),
  {      
    ...options,
    method: 'POST'
    
    
  }
);}




export const getLinkGuardianStudentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof linkGuardianStudent>>, TError,{id: string;studentId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof linkGuardianStudent>>, TError,{id: string;studentId: string}, TContext> => {

const mutationKey = ['linkGuardianStudent'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof linkGuardianStudent>>, {id: string;studentId: string}> = (props) => {
          const {id,studentId} = props ?? {};

          return  linkGuardianStudent(id,studentId,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type LinkGuardianStudentMutationResult = NonNullable<Awaited<ReturnType<typeof linkGuardianStudent>>>
    
    export type LinkGuardianStudentMutationError = Error

    export const useLinkGuardianStudent = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof linkGuardianStudent>>, TError,{id: string;studentId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof linkGuardianStudent>>,
        TError,
        {id: string;studentId: string},
        TContext
      > => {

      const mutationOptions = getLinkGuardianStudentMutationOptions(options);

      return useMutation(mutationOptions);
    }
    
export const getUnlinkGuardianStudentUrl = (id: string,
    studentId: string,) => {


  

  return `/api/guardians/${id}/students/${studentId}`
}

export const unlinkGuardianStudent = async (id: string,
    studentId: string, options?: RequestInit): Promise<Guardian> => {
  
  return customFetch<Guardian>(getUnlinkGuardianStudentUrl(id,studentId),
  {      
    ...options,
    method: 'DELETE'
    
    
  }
);}




export const getUnlinkGuardianStudentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlinkGuardianStudent>>, TError,{id: string;studentId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof unlinkGuardianStudent>>, TError,{id: string;studentId: string}, TContext> => {

const mutationKey = ['unlinkGuardianStudent'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unlinkGuardianStudent>>, {id: string;studentId: string}> = (props) => {
          const {id,studentId} = props ?? {};

          return  unlinkGuardianStudent(id,studentId,requestOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UnlinkGuardianStudentMutationResult = NonNullable<Awaited<ReturnType<typeof unlinkGuardianStudent>>>
    
    export type UnlinkGuardianStudentMutationError = Error

    export const useUnlinkGuardianStudent = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlinkGuardianStudent>>, TError,{id: string;studentId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof unlinkGuardianStudent>>,
        TError,
        {id: string;studentId: string},
        TContext
      > => {

      const mutationOptions = getUnlinkGuardianStudentMutationOptions(options);

      return useMutation(mutationOptions);
    }
